import * as React from 'react';
import { Combobox as ComboboxPrimitive } from '@base-ui/react';
import { X } from 'lucide-react';
import { cn } from '../../lib/utils';
import { Label } from '../Label';
import { Button } from '../Button';
import useI18n from '../../hooks/useI18n';
import type { ChipInputProps } from './types';
import { EditableChip } from './EditableChip';

// Split pasted or typed values on commas and line breaks.
const DEFAULT_SEPARATOR_PATTERN = /[,\n]+/;

function splitRawValue(rawValue: string, separatorPattern: RegExp) {
  return rawValue
    .split(separatorPattern)
    .map((part) => part.trim())
    .filter(Boolean);
}

export const ChipInput = React.forwardRef<HTMLInputElement, ChipInputProps>(
  (
    {
      value,
      defaultValue = [],
      onValueChange,
      label,
      error = false,
      removeButtonLabel,
      editable = false,
      validateEdit,
      addOnBlur = true,
      separatorPattern = DEFAULT_SEPARATOR_PATTERN,
      deduplicate = true,
      placeholder,
      chipClassName,
      chipLabelClassName,
      chipsContainerClassName,
      inputClassName,
      containerClassName,
      disabled,
      id,
      required,
      onBlur,
      onKeyDown,
      ...inputProps
    },
    ref,
  ) => {
    const { t } = useI18n();
    const [internalValue, setInternalValue] = React.useState(defaultValue);
    const [inputValue, setInputValue] = React.useState('');
    const [announcement, setAnnouncement] = React.useState('');
    const fieldRef = React.useRef<HTMLDivElement>(null);
    const generatedId = React.useId();
    const inputId = id ?? generatedId;
    const chips = value ?? internalValue;
    const isControlled = value !== undefined;
    const hasLabel = label != null;
    // Native controls keep chip navigation from intercepting text editing keys.
    const ChipsContainer = editable ? 'div' : ComboboxPrimitive.Chips;
    const Input = editable ? 'input' : ComboboxPrimitive.Input;
    const focusChip = (index: number) => {
      requestAnimationFrame(() => {
        const entries = fieldRef.current?.querySelectorAll(
          '[data-slot="editable-chip"]',
        );
        const target =
          entries?.[
            Math.min(index, entries.length - 1)
          ]?.querySelector<HTMLElement>('[data-slot="chip-edit"], input') ??
          fieldRef.current?.querySelector<HTMLInputElement>(
            '[data-slot="chip-input-control"]',
          );
        target?.focus();
      });
    };

    const getChipRemoveLabel = (chip: string) =>
      removeButtonLabel ? `${t('delete')} ${chip}` : chip;

    const updateValue = (nextValue: string[], announceRemoval = true) => {
      const shouldUpdate = onValueChange?.(nextValue);

      if (shouldUpdate === false) {
        return false;
      }

      const removedChips = chips.filter((chip) => !nextValue.includes(chip));

      if (announceRemoval && removedChips.length > 0) {
        setAnnouncement(
          removedChips.map((chip) => t('delete', { value: chip })).join(', '),
        );
      }

      if (!isControlled) {
        setInternalValue(nextValue);
      }

      return true;
    };

    const commitRawValue = (rawValue: string) => {
      const nextItems = splitRawValue(rawValue, separatorPattern);

      if (!nextItems.length) {
        setInputValue('');
        return;
      }

      const nextChips = [...chips];

      nextItems.forEach((item) => {
        if (!deduplicate || !nextChips.includes(item)) {
          nextChips.push(item);
        }
      });

      if (updateValue(nextChips)) {
        setInputValue('');
        return;
      }

      // Put the rejected text back, separator included. handleKeyDown consumes
      // the comma, so dropping it here would glue the rejected entry to
      // whatever is typed next instead of leaving two.
      setInputValue(rawValue);
    };

    const handleInputValueChange = (
      nextInputValue: string,
      eventDetails: ComboboxPrimitive.Root.ChangeEventDetails,
    ) => {
      // Base UI treats the text as a filter query and drops it when the combobox
      // closes. Here the text is the value itself, so only the user clears it.
      if (eventDetails.reason === 'input-clear') return;

      setInputValue(nextInputValue);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
      onKeyDown?.(event);
      if (
        event.defaultPrevented ||
        disabled ||
        inputProps.readOnly ||
        event.nativeEvent.isComposing
      )
        return;

      // Tab stays plain navigation (WCAG 2.1.2 No Keyboard Trap). Committing on
      // Tab meant preventing it, which held focus for good whenever the consumer
      // rejected the value. handleBlur commits the pending text instead.
      if (event.key !== 'Enter' && event.key !== ',') return;

      event.preventDefault();
      commitRawValue(event.key === ',' ? inputValue + event.key : inputValue);
    };

    const handleBlur = (event: React.FocusEvent<HTMLInputElement>) => {
      if (addOnBlur && inputValue.trim()) {
        commitRawValue(inputValue);
      }
      onBlur?.(event);
    };

    const content = (
      <>
        <div
          ref={fieldRef}
          className='eye:group/combobox-field eye:block eye:w-full'
        >
          <div
            className={cn(
              'eye:pb-6 eye:relative eye:flex eye:min-h-[44px] eye:w-full eye:items-start eye:rounded-md eye:border eye:bg-grey-5 eye:px-12 eye:py-8',
              'eye:focus-within:outline-2 eye:focus-within:outline-offset-3',
              disabled && 'eye:bg-grey-20',
              error
                ? 'eye:border-red-100 eye:hover:border-red-100'
                : 'hover:eye:border-black eye:border-grey-100',
              containerClassName,
            )}
          >
            {hasLabel ? (
              <Label
                htmlFor={inputId}
                className={cn(
                  'eye:body-s-highlight eye:absolute eye:top-0 eye:left-12 eye:z-10 eye:m-0 eye:max-w-[calc(100%-32px)] eye:-translate-y-1/2 eye:bg-label-gradient eye:px-[3px] eye:leading-none eye:whitespace-nowrap',
                  'eye:group-has-disabled/combobox-field:text-grey-disabled',
                  'eye:group-has-[[data-slot=chip-input-control][aria-invalid=true]]/combobox-field:text-red-100',
                )}
              >
                {label}
                {required ? <span aria-hidden='true'>*</span> : null}
              </Label>
            ) : null}
            <ChipsContainer
              role='group'
              aria-label={typeof label === 'string' ? label : undefined}
              className={cn(
                'eye:flex eye:w-full eye:flex-wrap eye:items-center eye:gap-4xs',
                chipsContainerClassName,
                chips.length > 0 && 'eye:mt-4xs',
              )}
            >
              {chips.map((chip, index) =>
                editable ? (
                  <EditableChip
                    key={deduplicate ? chip : index}
                    value={chip}
                    disabled={disabled || inputProps.readOnly}
                    className={chipClassName}
                    labelClassName={chipLabelClassName}
                    onSave={(nextChip) => {
                      if (
                        !nextChip ||
                        (deduplicate &&
                          chips.some(
                            (item, position) =>
                              position !== index && item === nextChip,
                          ))
                      ) {
                        return t('invalidValue');
                      }
                      const validationError = validateEdit?.(nextChip);
                      if (validationError) return validationError;
                      if (
                        !updateValue(
                          chips.map((item, position) =>
                            position === index ? nextChip : item,
                          ),
                          false,
                        )
                      ) {
                        return t('invalidValue');
                      }
                      setAnnouncement(`${t('save')} ${nextChip}`);
                      focusChip(index);
                      return null;
                    }}
                    onRemove={() => {
                      if (
                        updateValue(
                          chips.filter((_, position) => position !== index),
                        )
                      )
                        focusChip(index);
                    }}
                  />
                ) : (
                  <ComboboxPrimitive.Chip
                    key={`${chip}-${index}`}
                    data-slot='combobox-chip'
                    className='eye:contents'
                  >
                    <ComboboxPrimitive.ChipRemove
                      aria-label={getChipRemoveLabel(chip)}
                      tabIndex={0}
                      render={
                        <Button type='button' variant='ghost' size='xs' />
                      }
                      className={cn(
                        'eye:leading-normal! eye:h-auto eye:min-h-24 eye:max-w-full eye:min-w-0 eye:gap-4 eye:rounded-md eye:border eye:border-grey-40 eye:bg-grey-20 eye:px-12 eye:py-[2px] eye:text-14! eye:font-regular eye:wrap-break-word eye:whitespace-normal eye:ring-inset eye:hover:bg-grey-40 eye:focus-visible:ring-2 eye:focus-visible:ring-[var(--stroke-color-black)] eye:focus-visible:outline-none eye:has-[>svg]:px-12',
                        chipClassName,
                      )}
                    >
                      <span
                        className={cn('eye:text-grey-140', chipLabelClassName)}
                      >
                        {chip}
                      </span>
                      <X size={14} aria-hidden className='eye:shrink-0' />
                    </ComboboxPrimitive.ChipRemove>
                  </ComboboxPrimitive.Chip>
                ),
              )}

              <Input
                {...inputProps}
                {...(editable
                  ? {
                      value: inputValue,
                      onChange: (event: React.ChangeEvent<HTMLInputElement>) =>
                        setInputValue(event.target.value),
                    }
                  : {})}
                ref={ref}
                id={inputId}
                data-slot='chip-input-control'
                disabled={disabled}
                required={required && chips.length === 0}
                placeholder={placeholder}
                aria-invalid={error}
                className={cn(
                  'eye:body-m eye:min-h-32 eye:min-w-[8rem] eye:flex-1 eye:self-center eye:bg-transparent eye:outline-none eye:placeholder:text-grey-placeholder!',
                  disabled && 'eye:cursor-not-allowed eye:text-grey-disabled',
                  inputClassName,
                )}
                onKeyDown={handleKeyDown}
                onBlur={handleBlur}
              />
            </ChipsContainer>
          </div>
        </div>
        <span aria-live='polite' role='status' className='eye:sr-only'>
          {announcement}
        </span>
      </>
    );

    return editable ? (
      content
    ) : (
      <ComboboxPrimitive.Root<string, true>
        multiple
        items={chips}
        value={chips}
        inputValue={inputValue}
        itemToStringLabel={(item) => item}
        itemToStringValue={(item) => item}
        onValueChange={(nextValue) => updateValue(nextValue ?? [])}
        onInputValueChange={handleInputValueChange}
      >
        {content}
      </ComboboxPrimitive.Root>
    );
  },
);

ChipInput.displayName = 'ChipInput';
